import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  offlineStoreDataGuard,
  type StoreData,
} from '~domains/partner-application/entities/application';
import {
  getAgentSchema,
  type AgentFormValues,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_AGENTS,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { PhoneField } from '@breezeventures/ui-kit/phone-field/rhf';
import { Select } from '@breezeventures/ui-kit/select/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputDate } from '@breezeventures/web-ph-components/input-date';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';

type AgentFormProps = {
  agentDraft: AgentFormValues | null;
  pointsOfSales: StoreData[] | undefined;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: AgentFormValues) => Promise<void>;
};

export const AgentForm = ({
  agentDraft,
  pointsOfSales,
  onBack,
  onClose,
  onSubmit,
}: AgentFormProps) => {
  const offlinePointsOfSales =
    pointsOfSales?.filter(offlineStoreDataGuard) ?? [];

  const offlinePointsOfSalesIds = offlinePointsOfSales.map(
    (pos) => pos.details.uiId
  );
  const storeOptions =
    offlinePointsOfSales.map((store) => ({
      value: store.details.uiId,
      label: store.details.name,
    })) ?? [];

  const storeTitleMap = Object.fromEntries(
    storeOptions.map((opt) => [opt.value, opt.label])
  );

  const methods = useForm<AgentFormValues>({
    resolver: zodResolver(getAgentSchema(offlinePointsOfSalesIds)),
    mode: 'all',
    defaultValues: agentDraft ?? {
      uiId: uuidv4(),
      firstName: '',
      middleName: '',
      lastName: '',
      birthDate: '',
      email: '',
      mobilePhone: '',
      assignedPointsOfSales: [],
    },
  });

  useEffect(() => {
    if (agentDraft) {
      methods.trigger();
    }
  }, [agentDraft, methods]);

  const handleSubmit = async (values: AgentFormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_AGENTS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            submitButtonText="Save"
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="my-6">
            Add the agent&apos;s info
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <Controller
              name="firstName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="First name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="middleName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Middle name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="lastName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Last name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="birthDate"
              control={methods.control}
              render={(fieldProps) => (
                <InputDate {...fieldProps} label="Date of birth" />
              )}
            />

            <Controller
              name="email"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="E-mail"
                  labelPosition="inner"
                  inputMode="email"
                />
              )}
            />

            <Controller
              name="mobilePhone"
              control={methods.control}
              render={(fieldProps) => (
                <PhoneField
                  {...fieldProps}
                  label="Mobile number"
                  labelPosition="inner"
                  inputMode="tel"
                />
              )}
            />

            <Controller
              name="assignedPointsOfSales"
              control={methods.control}
              render={(props) => (
                <Select
                  {...props}
                  field={{
                    ...props.field,
                    onChange: (value) => {
                      if (typeof value === 'undefined') {
                        props.field.onChange([]);
                        return;
                      }
                      props.field.onChange(value);
                    },
                  }}
                  label="Stores"
                  labelPosition="inner"
                  showClear
                  multiple
                  renderValue={(values = []) =>
                    values.map((value) => storeTitleMap[value]).join(', ')
                  }
                >
                  {storeOptions.map(({ value, label }) => (
                    <Select.Option key={value} value={value}>
                      <Typography.Text>{label}</Typography.Text>
                      <Select.OptionIndicator multiple />
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
